import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Grid,
  Modal,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 1,
  maxHeight: '90vh',
  overflow: 'auto'
};

const OperatorView = ({ open, onClose, operator }) => {
  const userData = useSelector((state) => state.user.data);
  const isAdmin = userData?.role === 'admin';

  const formatDate = (date) => {
    if (!date) return 'N/A';
    if (date instanceof Date) {
      return date.toLocaleString();
    }
    if (date.toDate && typeof date.toDate === 'function') {
      return date.toDate().toLocaleString();
    }
    return 'N/A';
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={modalStyle}>
        {/* Header */}
        <Box sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #E5E7EB'
        }}>
          <Typography variant="h6" component="h2">
            Operator Details
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        {operator && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Operator Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{operator.operatorName || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Operator Type</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{operator.operatorType || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Contact Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                  Contact Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Central SPOC Name</Typography>
                    <Typography variant="body1">{operator.centralSpocName || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Central SPOC Email</Typography>
                    <Typography variant="body1">{operator.centralSpocEmail || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Central SPOC Phone</Typography>
                    <Typography variant="body1">{operator.centralSpocPhoneNumber || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Terms & Conditions */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                  Terms & Conditions
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Annual Escalation</Typography>
                    <Typography variant="body1">{operator.annualEscalation || 'N/A'}%</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Security Deposit (months)</Typography>
                    <Typography variant="body1">{operator.deposit || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Lock-in Period (months)</Typography>
                    <Typography variant="body1">{operator.lockinPeriod || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Notice Period (months)</Typography>
                    <Typography variant="body1">{operator.noticePeriod || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Additional Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                  Additional Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Corporate Profile</Typography>
                    <Typography variant="body1" sx={{
                      p: 1.5,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      minHeight: '60px'
                    }}>
                      {operator.corporateProfile || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Registered Address</Typography>
                    <Typography variant="body1" sx={{
                      p: 1.5,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      minHeight: '60px'
                    }}>
                      {operator.registeredAddress || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Standard Terms</Typography>
                    <Typography variant="body1" sx={{
                      p: 1.5,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      minHeight: '60px'
                    }}>
                      {operator.standardTerms || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Record Details - Only visible to admin */}
              {isAdmin && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                    Record Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Operator ID</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>{operator.id || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Created By</Typography>
                      <Typography variant="body1">{operator.createdBy || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Created At</Typography>
                      <Typography variant="body1">{formatDate(operator.createdAt)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Last Updated By</Typography>
                      <Typography variant="body1">{operator.lastUpdatedBy || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Last Updated At</Typography>
                      <Typography variant="body1">{formatDate(operator.lastUpdatedAt)}</Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default OperatorView;
